import { ChevronDown, Users, ClipboardList, HeartHandshake } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import ImpactChart from "./impact-chart";

const EMPTY_METRIC = { current: 0, previous: 0, changePercent: 0 };

const formatNumber = (n) => (n ?? 0).toLocaleString();

const DATE_RANGES = [
  { value: "month", label: "This Month" },
  { value: "quarter", label: "Last 3 Months" },
  { value: "ytd", label: "Year to Date" },
];

export default function MainImpactDashboard() {
  const [openMenu, setOpenMenu] = useState(null);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Applied filters (drive the fetch) and the pending selection in the UI.
  const [filters, setFilters] = useState({ range: "month", programType: "", zip: "" });
  // Filter option lists captured once from the first unfiltered response.
  const [programOptions, setProgramOptions] = useState([]);
  const [zipOptions, setZipOptions] = useState([]);

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchImpact(filters, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchImpact = async (activeFilters, captureOptions = false) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilters.range) params.set("range", activeFilters.range);
      if (activeFilters.programType) params.set("programType", activeFilters.programType);
      if (activeFilters.zip) params.set("zip", activeFilters.zip);

      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.GET_IMPACT_DASHBOARD}?${params.toString()}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        }
      );
      const result = await response.json();
      if (response.ok && result.statusCode === 200) {
        setData(result.data);
        if (captureOptions) {
          setProgramOptions(
            (result.data.requestsByProgramType || [])
              .filter((p) => p.categoryValue)
              .map((p) => ({ label: p.name, value: p.categoryValue }))
          );
          setZipOptions((result.data.impactByZip || []).map((z) => z.zip));
        }
      } else {
        toast.error(result.message || "Failed to load impact data");
      }
    } catch (error) {
      console.error("Error fetching impact dashboard:", error);
      toast.error("Failed to load impact data");
    } finally {
      setIsLoading(false);
    }
  };

  const selectFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setOpenMenu(null);
  };

  const applyFilters = () => fetchImpact(filters);

  const resetFilters = () => {
    const cleared = { range: "month", programType: "", zip: "" };
    setFilters(cleared);
    fetchImpact(cleared);
  };

  const peopleHelped = data?.peopleHelped || EMPTY_METRIC;
  const activePrograms = data?.activePrograms || EMPTY_METRIC;
  const ngoCollaborations = data?.ngoCollaborations || EMPTY_METRIC;

  const rangeLabel = DATE_RANGES.find((r) => r.value === filters.range)?.label || "Date Range";
  const programLabel =
    programOptions.find((p) => p.value === filters.programType)?.label || "All Programs";
  const zipLabel = filters.zip || "All ZIP Codes";

  const kpis = [
    {
      label: "People Helped",
      caption: "This Period",
      metric: peopleHelped,
      Icon: Users,
      accent: "#3FC4E3",
    },
    {
      label: "Active Programs",
      caption: "Running Total",
      metric: activePrograms,
      Icon: ClipboardList,
      accent: "#08873B",
    },
    {
      label: "NGO Collaborations",
      caption: "Formed",
      metric: ngoCollaborations,
      Icon: HeartHandshake,
      accent: "#006881",
    },
  ];

  const DeltaPill = ({ value }) => {
    const positive = value >= 0;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-popinsSemiBold ${
          positive ? "bg-[#08873B]/10 text-[#08873B]" : "bg-[#E83808]/10 text-[#E83808]"
        }`}
      >
        {positive ? "↑" : "↓"} {Math.abs(value)}%
      </span>
    );
  };

  const FilterChip = ({ id, label, options }) => (
    <div className="relative">
      <button
        className="px-4 py-2 border rounded-full text-[#121417] font-popinsMedium cursor-pointer bg-[#F0F2F5] shadow-sm hover:bg-gray-100 focus:outline-none flex items-center text-sm gap-x-2"
        onClick={() => setOpenMenu(openMenu === id ? null : id)}
      >
        {label}
        <ChevronDown size={18} />
      </button>
      {openMenu === id && (
        <ul className="absolute mt-1 w-56 max-h-60 overflow-y-auto bg-white text-[#121417] text-sm border rounded-md font-popinsRegular shadow-lg z-50">
          {options.map((opt) => (
            <li
              key={opt.value ?? opt}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-black/10 last:border-b-0"
              onClick={() => selectFilter(opt.key, opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="lg:w-[95%] mb-5">
      <div>
        <h1 className="font-popinsBold text-xl lg:text-2xl">Program Impact Viewer</h1>
        <p className="font-popinsRegular text-sm text-black/50 mt-2">
          Monitor and measure the effectiveness of your community initiatives.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-y-3 gap-x-3 mt-8" ref={menuRef}>
        <FilterChip
          id="date"
          label={rangeLabel}
          options={DATE_RANGES.map((r) => ({ key: "range", label: r.label, value: r.value }))}
        />
        <FilterChip
          id="program"
          label={programLabel}
          options={[
            { key: "programType", label: "All Programs", value: "" },
            ...programOptions.map((p) => ({ key: "programType", label: p.label, value: p.value })),
          ]}
        />
        <FilterChip
          id="zip"
          label={zipLabel}
          options={[
            { key: "zip", label: "All ZIP Codes", value: "" },
            ...zipOptions.map((z) => ({ key: "zip", label: z, value: z })),
          ]}
        />

        <button
          onClick={applyFilters}
          className="py-2 px-5 bg-themeColor text-white font-popinsSemiBold text-sm rounded-full shadow
             transition duration-300 ease-in-out hover:bg-[#3FC4E3] active:scale-95 focus:outline-none"
        >
          Apply Filters
        </button>
        <button
          onClick={resetFilters}
          className="py-2 px-5 bg-[#F0F5F2] text-black font-popinsSemiBold text-sm rounded-full shadow
             transition duration-300 ease-in-out hover:bg-gray-200 active:scale-95 focus:outline-none"
        >
          Reset
        </button>
      </div>

      {/* KPI cards — white with faded background figure */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="relative overflow-hidden bg-white rounded-xl shadow border border-border p-5"
            style={{ borderTop: `4px solid ${kpi.accent}` }}
          >
            {/* Faded background number */}
            <span
              className="pointer-events-none absolute -top-2 right-2 font-popinsBold leading-none select-none"
              style={{ fontSize: "5.5rem", color: kpi.accent, opacity: 0.1 }}
            >
              {isLoading ? "" : formatNumber(kpi.metric.current)}
            </span>

            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${kpi.accent}1A` }}
            >
              <kpi.Icon size={20} style={{ color: kpi.accent }} />
            </div>

            <p className="uppercase tracking-wide text-xs text-black/50 font-popinsSemiBold mt-4">
              {kpi.label}
            </p>
            <p className="font-popinsBold text-3xl mt-1">
              {isLoading ? "--" : formatNumber(kpi.metric.current)}
            </p>

            <div className="flex items-center gap-2 mt-2">
              {!isLoading && <DeltaPill value={kpi.metric.changePercent} />}
              <span className="text-xs text-black/50 font-popinsRegular">{kpi.caption}</span>
            </div>
            {!isLoading && (
              <p className="text-xs text-black/40 font-popinsRegular mt-3">
                vs prev: {formatNumber(kpi.metric.previous)}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-[300px] flex items-center justify-center text-black/40 font-popinsRegular">
            Loading impact data…
          </div>
        ) : (
          <ImpactChart data={data} />
        )}
      </div>
    </div>
  );
}
